<script lang="ts">
    /**
     * GuidePage - 앱 사용 가이드
     *
     * 모던 미니멀 디자인 (홈페이지 테마 통일)
     * 한/영 전환 지원
     */

    type Lang = 'ko' | 'en';
    let lang: Lang = $state('ko');
    let activeFaq = $state<number | null>(null);

    function switchLang(newLang: Lang) {
        lang = newLang;
    }

    function toggleFaq(index: number) {
        activeFaq = activeFaq === index ? null : index;
    }

    function t(ko: string, en: string): string {
        return lang === 'ko' ? ko : en;
    }

    /**
     * 가이드 글(아티클) 카드 — 서버가 `window.JadEye.articles` 로 주입한다(Seo::articles() 순회, 애드센스 재심사 대응 작업 4).
     *
     * 🚨 슬러그·제목을 여기 적지 않는다 — 새 글이 발행되면 카드가 저절로 늘어난다(함정 17: 제목↔앵커 불일치).
     * ℹ️ 글 본문이 한국어뿐이라 카드의 제목·요약은 영문 전환 때도 원문 그대로 둔다.
     */
    interface GuideArticle {
        path: string;
        title: string;
        description: string;
        updatedAt: string;
    }

    const articles: GuideArticle[] = (window as unknown as { JadEye?: { articles?: GuideArticle[] } }).JadEye?.articles ?? [];

    const tocItems = [
        { id: 'getting-started', ko: '시작하기', en: 'Getting Started' },
        { id: 'dashboard', ko: '홈 (대시보드)', en: 'Dashboard' },
        { id: 'record', ko: '건강 기록하기', en: 'Health Recording' },
        { id: 'quick-record', ko: '빠른 기록', en: 'Quick Record' },
        { id: 'smart', ko: 'AI 스마트 분석', en: 'AI Smart Analysis' },
        { id: 'social', ko: '소셜', en: 'Social' },
        { id: 'challenge', ko: '챌린지', en: 'Challenge' },
        { id: 'voice', ko: '음성 어시스턴트', en: 'Voice Assistant' },
        { id: 'cs-chat', ko: '고객지원 챗봇', en: 'CS Chatbot' },
        { id: 'settings', ko: '설정', en: 'Settings' },
        { id: 'faq', ko: '자주 묻는 질문', en: 'FAQ' },
    ];

    const permissions = [
        {
            ko: 'Google Health Connect',
            en: 'Google Health Connect',
            descKo: '걸음 수, 심박수, 운동 데이터를 자동으로 가져옵니다.',
            descEn: 'Automatically imports steps, heart rate, and workout data.',
        },
        {
            ko: '마이크',
            en: 'Microphone',
            descKo: '음성 어시스턴트 기능을 사용하기 위해 필요합니다.',
            descEn: 'Required for the voice assistant feature.',
        },
        {
            ko: '알림',
            en: 'Notifications',
            descKo: '약물 복용 알림, 목표 달성 알림 등을 받을 수 있습니다.',
            descEn: 'Receive medication reminders, goal achievements, and more.',
        },
    ];

    const dashboardCards = [
        {
            ko: '오늘의 요약',
            en: "Today's Summary",
            descKo: '걸음 수, 소모 칼로리, 운동 시간 등 오늘의 활동 요약을 확인하세요.',
            descEn: 'Check your daily activity summary including steps, calories burned, and workout time.',
        },
        {
            ko: '목표 진행률',
            en: 'Goal Progress',
            descKo: '설정한 건강 목표의 달성률을 프로그레스 바로 실시간 확인하세요.',
            descEn: 'Track your health goal achievements with real-time progress bars.',
        },
        {
            ko: '최근 건강 데이터',
            en: 'Recent Health Data',
            descKo: '최근 기록한 혈압, 체중, 심박수 등의 건강 지표를 빠르게 확인하세요.',
            descEn: 'Quickly view your recent health metrics such as blood pressure, weight, and heart rate.',
        },
    ];

    const tabItems = [
        { ko: '홈', en: 'Home' },
        { ko: '기록', en: 'Record' },
        { ko: '스마트', en: 'Smart' },
        { ko: '소셜', en: 'Social' },
        { ko: '챌린지', en: 'Challenge' },
    ];

    const healthMetrics = [
        {
            ko: '혈압',
            en: 'Blood Pressure',
            items: [
                { ko: '수축기/이완기 혈압 입력', en: 'Enter systolic/diastolic values' },
                { ko: '맥박 수 함께 기록', en: 'Record pulse rate together' },
                { ko: '시간대별 그래프 확인', en: 'View time-based graphs' },
            ],
        },
        {
            ko: '체중',
            en: 'Weight',
            items: [
                { ko: '체중 입력 시 BMI 자동 계산', en: 'Automatic BMI calculation' },
                { ko: '목표 체중 대비 진행률 표시', en: 'Progress towards target weight' },
                { ko: '체중 변화 추이 그래프', en: 'Weight trend graph' },
            ],
        },
        {
            ko: '심박수 & 체온',
            en: 'Heart Rate & Temperature',
            items: [
                { ko: '심박수 수동 입력 또는 자동 연동', en: 'Manual or auto-sync heart rate' },
                { ko: '체온 기록 및 발열 알림', en: 'Temperature log and fever alerts' },
            ],
        },
    ];

    const aiFeatures = [
        {
            ko: 'AI 건강 분석',
            en: 'AI Health Analysis',
            descKo: '기록된 건강 데이터를 종합 분석하여 건강 상태 리포트를 생성합니다.',
            descEn: 'Generates a comprehensive health status report from your recorded data.',
        },
        {
            ko: 'AI 운동 추천',
            en: 'AI Workout Recommendation',
            descKo: '건강 상태와 운동 히스토리를 바탕으로 맞춤 운동 프로그램을 추천합니다.',
            descEn: 'Recommends customized workout programs based on your health and exercise history.',
        },
        {
            ko: 'AI 영양 분석',
            en: 'AI Nutrition Analysis',
            descKo: '식단 기록을 분석하여 영양 균형과 개선점을 알려줍니다.',
            descEn: 'Analyzes your diet to identify nutritional balance and areas for improvement.',
        },
        {
            ko: 'AI 인사이트',
            en: 'AI Insights',
            descKo: '건강 데이터의 패턴과 트렌드를 발견하여 의미 있는 인사이트를 제공합니다.',
            descEn: 'Discovers patterns and trends in your health data to provide meaningful insights.',
        },
    ];

    const socialFeatures = [
        {
            ko: '포인트',
            en: 'Points',
            descKo: '건강 기록, 운동, 목표 달성 등 다양한 활동으로 포인트를 적립하세요. 포인트는 AI 크레딧으로 교환할 수 있습니다.',
            descEn: 'Earn points through health recordings, workouts, and goal achievements. Points can be exchanged for AI credits.',
        },
        {
            ko: '배지',
            en: 'Badges',
            descKo: '특별한 성취를 달성하면 배지를 획득합니다. 희귀한 배지를 모아보세요!',
            descEn: 'Earn badges for special achievements. Collect rare badges!',
        },
        {
            ko: '랭킹',
            en: 'Rankings',
            descKo: '다른 사용자들과 포인트 랭킹을 비교하며 건강한 경쟁을 즐기세요.',
            descEn: 'Compare your rankings with other users and enjoy healthy competition.',
        },
        {
            ko: '팔로우 & 커뮤니티',
            en: 'Follow & Community',
            descKo: '다른 사용자를 팔로우하고 서로의 건강 여정을 응원하세요.',
            descEn: "Follow other users and support each other's health journeys.",
        },
    ];

    const settingsCards = [
        {
            ko: '프로필 설정',
            en: 'Profile Settings',
            descKo: '이름, 생년월일, 키, 성별 등 기본 정보를 관리합니다.',
            descEn: 'Manage basic info like name, birth date, height, and gender.',
        },
        {
            ko: '알림 설정',
            en: 'Notification Settings',
            descKo: '약물 알림, 운동 리마인더, 목표 알림 등을 설정합니다.',
            descEn: 'Configure medication reminders, workout reminders, and goal notifications.',
        },
        {
            ko: '테마 설정',
            en: 'Theme Settings',
            descKo: '앱의 외관과 테마를 취향에 맞게 변경합니다.',
            descEn: "Customize the app's appearance and theme to your preference.",
        },
        {
            ko: '단위 설정',
            en: 'Unit Settings',
            descKo: '체중(kg/lbs), 키(cm/ft), 온도(℃/℉) 등 측정 단위를 설정합니다.',
            descEn: 'Set measurement units for weight (kg/lbs), height (cm/ft), temperature (℃/℉), etc.',
        },
        {
            ko: '목표 설정',
            en: 'Goal Settings',
            descKo: '일일 걸음 수, 운동 시간, 칼로리 소모 등의 목표를 설정합니다.',
            descEn: 'Set daily goals for steps, workout duration, calorie burn, and more.',
        },
        {
            ko: '개인정보 & 구독',
            en: 'Privacy & Subscription',
            descKo: '개인정보 설정, 데이터 관리, 구독 상태를 확인합니다.',
            descEn: 'Manage privacy settings, data, and subscription status.',
        },
    ];

    const faqItems = [
        {
            qKo: '제이드아이는 무료인가요?',
            qEn: 'Is JadEye free?',
            aKo: '네, 제이드아이의 기본 기능(건강 기록, 운동 추적, 소셜 등)은 무료로 사용할 수 있습니다. AI 스마트 분석 기능은 크레딧이 필요하며, 활동을 통해 크레딧을 획득하거나 상점에서 구매할 수 있습니다.',
            aEn: "Yes, JadEye's basic features (health recording, workout tracking, social, etc.) are free. AI Smart Analysis requires credits, which can be earned through activities or purchased in the shop.",
        },
        {
            qKo: '데이터는 안전한가요?',
            qEn: 'Is my data safe?',
            aKo: '제이드아이는 로컬 우선 저장 방식을 채택하고 있어 모든 건강 데이터가 기기에 먼저 저장됩니다. 서버와의 통신은 암호화되어 있으며, Google Firebase 인증을 통해 안전하게 보호됩니다.',
            aEn: 'JadEye uses a local-first storage approach, saving all health data on your device first. Server communication is encrypted, and your account is securely protected through Google Firebase authentication.',
        },
        {
            qKo: '인터넷 없이도 사용할 수 있나요?',
            qEn: 'Can I use it without internet?',
            aKo: '네! 제이드아이는 오프라인 우선 설계입니다. 인터넷 없이도 건강 데이터 기록, 조회가 가능하며, 인터넷에 연결되면 자동으로 서버와 동기화됩니다.',
            aEn: 'Yes! JadEye is designed offline-first. You can record and view health data without internet, and data will automatically sync with the server when connected.',
        },
        {
            qKo: '어떤 언어를 지원하나요?',
            qEn: 'What languages are supported?',
            aKo: '제이드아이는 한국어, 영어, 일본어, 중국어를 지원합니다. 기기의 언어 설정에 따라 자동으로 전환됩니다.',
            aEn: "JadEye supports Korean, English, Japanese, and Chinese. The app language automatically switches based on your device's language setting.",
        },
        {
            qKo: 'Google Health Connect와 어떻게 연동하나요?',
            qEn: 'How do I connect with Google Health Connect?',
            aKo: '앱 첫 실행 시 권한 설정에서 Health Connect 권한을 허용하면 자동으로 연동됩니다. 이미 설치한 경우 [설정 → 권한 설정]에서 Health Connect 권한을 활성화할 수 있습니다. Google Health Connect 앱이 기기에 설치되어 있어야 합니다.',
            aEn: 'Grant Health Connect permissions during the initial setup, and it will automatically sync. If already installed, enable it in [Settings → Permission Settings]. The Google Health Connect app must be installed on your device.',
        },
        {
            qKo: 'iOS(아이폰)에서도 사용할 수 있나요?',
            qEn: 'Is it available on iOS (iPhone)?',
            aKo: '네! 제이드아이는 Android와 iOS 모두 지원합니다. App Store에서 "제이드아이"를 검색하여 다운로드하세요. iOS 버전에서는 Google Health Connect 대신 Apple HealthKit과 연동됩니다.',
            aEn: 'Yes! JadEye supports both Android and iOS. Search for "JadEye" in the App Store to download. The iOS version integrates with Apple HealthKit instead of Google Health Connect.',
        },
        {
            qKo: '계정을 삭제하고 싶어요.',
            qEn: 'I want to delete my account.',
            aKo: '[설정 → 개인정보 설정]에서 계정 삭제를 요청할 수 있습니다. 삭제 시 모든 건강 데이터와 개인정보가 영구적으로 삭제됩니다.',
            aEn: 'You can request account deletion in [Settings → Privacy Settings]. All health data and personal information will be permanently deleted.',
        },
        {
            qKo: '문의는 어떻게 하나요?',
            qEn: 'How can I contact support?',
            aKo: '앱 내 CS 챗봇을 이용하시거나, 챗봇에서 해결되지 않으면 이메일 에스컬레이션을 통해 담당자에게 문의가 전달됩니다. 또는 cs@jadeye.net으로 직접 이메일을 보내실 수 있습니다.',
            aEn: 'Use the in-app CS Chatbot, or if unresolved, your inquiry will be escalated via email to our support team. You can also email us directly at cs@jadeye.net.',
        },
    ];

    function scrollToSection(id: string) {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }
</script>

<!-- title·description 은 config/seo.php 가 SSOT (LandingApp.applyMeta 참고).
     한/영 토글은 URL 을 바꾸지 않아 영어판이 색인되지 않으므로, title 까지 흔들 이유가 없다. -->

<div class="guide-page">
    <!-- Hero -->
    <header class="guide-hero">
        <div class="guide-hero-content">
            <div class="lang-switcher">
                <button class="lang-btn" class:active={lang === 'ko'} onclick={() => switchLang('ko')}>한국어</button>
                <button class="lang-btn" class:active={lang === 'en'} onclick={() => switchLang('en')}>English</button>
            </div>
            <div class="guide-hero-badge">
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    ><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg
                >
                {t('앱 사용 가이드', 'App User Guide')}
            </div>
            <h1 class="guide-hero-title">{t('제이드아이 가이드', 'JadEye Guide')}</h1>
            <p class="guide-hero-subtitle">{t('제이드아이의 모든 기능을 쉽게 알아보세요', 'Learn all features of JadEye easily')}</p>
        </div>
    </header>

    <!-- 가이드 글 — 앱 사용설명서와 별개로 한 주제를 깊게 다룬 서버 렌더 글로 가는 카드(목록은 서버 주입) -->
    {#if articles.length > 0}
        <section class="guide-articles" aria-labelledby="guide-articles-title">
            <h2 id="guide-articles-title" class="guide-articles-title">{t('더 깊게 읽는 가이드 글', 'In-depth guide articles')}</h2>
            <p class="guide-articles-subtitle">
                {t('앱 사용법 너머, 걷기와 함께하는 활동을 한 편씩 자세히 정리했어요.', 'Longer reads on walking and playing together (in Korean).')}
            </p>
            <ul class="guide-articles-grid">
                {#each articles as article}
                    <li>
                        <a class="guide-article-card" href={article.path}>
                            <span class="guide-article-name">{article.title}</span>
                            <span class="guide-article-desc">{article.description}</span>
                            <span class="guide-article-date">{t('최종 수정', 'Updated')} {article.updatedAt}</span>
                        </a>
                    </li>
                {/each}
            </ul>
        </section>
    {/if}

    <!-- TOC -->
    <nav class="guide-toc">
        <div class="toc-grid">
            {#each tocItems as item, i}
                <button class="toc-item" onclick={() => scrollToSection(item.id)}>
                    <span class="toc-num">{i + 1}</span>
                    <span>{t(item.ko, item.en)}</span>
                </button>
            {/each}
        </div>
    </nav>

    <!-- Content -->
    <div class="guide-content">
        <!-- 1. Getting Started -->
        <section id="getting-started" class="guide-section">
            <h2 class="section-title"><span class="section-num">1</span> {t('시작하기', 'Getting Started')}</h2>

            <h3 class="subsection-title">{t('앱 설치', 'App Installation')}</h3>
            <ol class="steps-list">
                <li>
                    {t(
                        'Google Play 스토어 또는 App Store에서 "제이드아이"를 검색합니다.',
                        'Search for "JadEye" in the Google Play Store or App Store.',
                    )}
                </li>
                <li>{t('설치 버튼을 눌러 앱을 다운로드합니다.', 'Tap Install to download the app.')}</li>
                <li>{t('설치 완료 후 열기를 눌러 앱을 실행합니다.', 'After installation, tap Open to launch the app.')}</li>
            </ol>

            <h3 class="subsection-title">{t('회원가입 및 로그인', 'Sign Up & Login')}</h3>
            <p class="guide-text">
                {t(
                    '제이드아이는 Google 계정으로 간편하게 로그인할 수 있습니다. 별도의 회원가입 절차가 필요 없습니다.',
                    'JadEye offers simple login with your Google account. No separate registration is required.',
                )}
            </p>
            <ol class="steps-list">
                <li>{t('앱을 실행하면 로그인 화면이 나타납니다.', 'The login screen appears when you launch the app.')}</li>
                <li>{t('"Google로 로그인" 버튼을 탭합니다.', 'Tap the "Sign in with Google" button.')}</li>
                <li>{t('사용할 Google 계정을 선택합니다.', 'Select your Google account.')}</li>
                <li>
                    {t('로그인이 완료되면 자동으로 홈 화면으로 이동합니다.', "After login, you'll be automatically redirected to the home screen.")}
                </li>
            </ol>

            <h3 class="subsection-title">{t('권한 설정', 'Permission Settings')}</h3>
            <p class="guide-text">
                {t('제이드아이의 모든 기능을 사용하려면 다음 권한이 필요합니다.', 'The following permissions are needed for full functionality.')}
            </p>
            <div class="card-grid">
                {#each permissions as perm}
                    <div class="guide-card">
                        <h4 class="card-title">{t(perm.ko, perm.en)}</h4>
                        <p class="card-desc">{t(perm.descKo, perm.descEn)}</p>
                    </div>
                {/each}
            </div>
            <div class="tip-box">
                <svg class="box-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    ><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg
                >
                <span
                    ><strong>TIP:</strong>
                    {t(
                        '권한은 나중에 [설정 → 권한 설정]에서 언제든 변경할 수 있습니다.',
                        'Permissions can be changed anytime in [Settings → Permission Settings].',
                    )}</span
                >
            </div>
        </section>

        <!-- 2. Dashboard -->
        <section id="dashboard" class="guide-section">
            <h2 class="section-title"><span class="section-num">2</span> {t('홈 (대시보드)', 'Home (Dashboard)')}</h2>
            <p class="guide-text">
                {t(
                    '홈 화면은 여러분의 건강 상태를 한눈에 볼 수 있는 대시보드입니다.',
                    'The home screen is a dashboard that gives you an overview of your health at a glance.',
                )}
            </p>
            <div class="card-grid">
                {#each dashboardCards as card}
                    <div class="guide-card">
                        <h4 class="card-title">{t(card.ko, card.en)}</h4>
                        <p class="card-desc">{t(card.descKo, card.descEn)}</p>
                    </div>
                {/each}
            </div>

            <h3 class="subsection-title">{t('앱 네비게이션', 'App Navigation')}</h3>
            <p class="guide-text">{t('화면 하단에 5개의 메인 탭이 있습니다.', 'There are 5 main tabs at the bottom of the screen.')}</p>
            <div class="tab-preview">
                {#each tabItems as tab}
                    <div class="tab-item">
                        <span class="tab-label">{t(tab.ko, tab.en)}</span>
                    </div>
                {/each}
            </div>
            <div class="tip-box">
                <svg class="box-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    ><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg
                >
                <span
                    ><strong>TIP:</strong>
                    {t(
                        '화면 왼쪽 상단의 메뉴를 눌러 설정, 상점, 알림 내역 등에 접근할 수 있습니다.',
                        'Tap the menu at the top left to access settings, shop, notification history, and more.',
                    )}</span
                >
            </div>
        </section>

        <!-- 3. Health Recording -->
        <section id="record" class="guide-section">
            <h2 class="section-title"><span class="section-num">3</span> {t('건강 기록하기', 'Health Recording')}</h2>
            <p class="guide-text">
                {t(
                    '기록 탭에서는 건강 데이터, 운동, 영양, 약물 복용, 증상을 기록할 수 있습니다.',
                    'The Record tab lets you log health data, workouts, nutrition, medication, and symptoms.',
                )}
            </p>

            <h3 class="subsection-title">{t('건강 지표', 'Health Metrics')}</h3>
            <div class="card-grid">
                {#each healthMetrics as metric}
                    <div class="guide-card">
                        <h4 class="card-title">{t(metric.ko, metric.en)}</h4>
                        <ul class="card-list">
                            {#each metric.items as item}
                                <li>{t(item.ko, item.en)}</li>
                            {/each}
                        </ul>
                    </div>
                {/each}
            </div>

            <h4 class="subsection-title-sm">{t('건강 기록 방법', 'How to Record Health Data')}</h4>
            <ol class="steps-list">
                <li>{t('하단 탭에서 기록 탭을 선택합니다.', 'Select the Record tab from the bottom navigation.')}</li>
                <li>{t('상단에서 건강 탭을 선택합니다.', 'Select the Health sub-tab at the top.')}</li>
                <li>{t('기록할 항목(혈압, 체중 등)을 선택합니다.', 'Choose the item to record (blood pressure, weight, etc.).')}</li>
                <li>{t('수치를 입력하고 저장을 누릅니다.', 'Enter the values and tap Save.')}</li>
            </ol>

            <h3 class="subsection-title">{t('운동 추적', 'Workout Tracking')}</h3>
            <div class="card-grid cols-2">
                <div class="guide-card">
                    <h4 class="card-title">{t('운동 기록', 'Workout Log')}</h4>
                    <ul class="card-list">
                        <li>{t('다양한 운동 유형 선택 (달리기, 웨이트, 요가 등)', 'Choose workout types (running, weights, yoga, etc.)')}</li>
                        <li>{t('운동 시간, 거리, 소모 칼로리 기록', 'Log duration, distance, and calories burned')}</li>
                        <li>{t('운동 세션 타이머 기능', 'Workout session timer')}</li>
                        <li>{t('Google Health Connect에서 자동 가져오기', 'Auto-import from Google Health Connect')}</li>
                    </ul>
                </div>
                <div class="guide-card">
                    <h4 class="card-title">{t('운동 템플릿', 'Workout Templates')}</h4>
                    <ul class="card-list">
                        <li>{t('자주 하는 운동을 템플릿으로 저장', 'Save frequent workouts as templates')}</li>
                        <li>{t('한 번의 탭으로 빠르게 기록 시작', 'Start recording with a single tap')}</li>
                    </ul>
                </div>
            </div>

            <h3 class="subsection-title">{t('약물 & 증상 관리', 'Medication & Symptom Management')}</h3>
            <div class="card-grid cols-2">
                <div class="guide-card">
                    <h4 class="card-title">{t('약물 복용 기록', 'Medication Log')}</h4>
                    <ul class="card-list">
                        <li>{t('복용 중인 약물 이름과 용량 등록', 'Register medication names and dosages')}</li>
                        <li>{t('복용 시간 기록 및 알림 설정', 'Log intake times and set reminders')}</li>
                        <li>{t('복용 히스토리 조회', 'View medication history')}</li>
                    </ul>
                </div>
                <div class="guide-card">
                    <h4 class="card-title">{t('증상 기록', 'Symptom Log')}</h4>
                    <ul class="card-list">
                        <li>{t('증상 유형 선택 (두통, 복통 등)', 'Select symptom type (headache, stomachache, etc.)')}</li>
                        <li>{t('심각도 평가 (1~10단계)', 'Severity rating (1-10 scale)')}</li>
                        <li>{t('메모로 상세 상황 기록', 'Add detailed notes')}</li>
                    </ul>
                </div>
            </div>

            <div class="warning-box">
                <svg class="box-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    ><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line
                        x1="12"
                        y1="9"
                        x2="12"
                        y2="13"
                    /><line x1="12" y1="17" x2="12.01" y2="17" /></svg
                >
                <span
                    >{t(
                        '제이드아이의 건강 데이터는 의료 진단 목적이 아닌 개인 건강 관리 참고용입니다. 건강 이상 시 반드시 의료 전문가와 상담하세요.',
                        "JadEye's health data is for personal health management reference only, not for medical diagnosis. Always consult a healthcare professional for health concerns.",
                    )}</span
                >
            </div>
        </section>

        <!-- 4. Quick Record -->
        <section id="quick-record" class="guide-section">
            <h2 class="section-title"><span class="section-num">4</span> {t('빠른 기록', 'Quick Record')}</h2>
            <p class="guide-text">
                {t(
                    '화면 하단의 + 플로팅 버튼(FAB)을 누르면 어떤 화면에서든 빠르게 건강 데이터를 기록할 수 있습니다.',
                    'Tap the + floating button (FAB) at the bottom to quickly record health data from any screen.',
                )}
            </p>
            <ol class="steps-list">
                <li>{t('화면 하단 중앙의 + 버튼을 탭합니다.', 'Tap the + button at the bottom center of the screen.')}</li>
                <li>
                    {t(
                        '바텀 시트에서 기록 유형을 선택합니다: 혈압 · 체중 · 운동 · 약물 · 증상',
                        'Select the recording type from the bottom sheet: Blood Pressure · Weight · Workout · Medication · Symptom',
                    )}
                </li>
                <li>{t('간편 입력 화면에서 수치를 입력하고 저장합니다.', 'Enter values in the quick input screen and tap Save.')}</li>
            </ol>
            <div class="tip-box">
                <svg class="box-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    ><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg
                >
                <span
                    ><strong>TIP:</strong>
                    {t(
                        '빠른 기록은 가장 자주 쓰는 항목만 간략화된 입력 화면을 제공합니다.',
                        'Quick Record provides a simplified input screen for the most common items.',
                    )}</span
                >
            </div>
        </section>

        <!-- 5. AI Smart -->
        <section id="smart" class="guide-section">
            <h2 class="section-title"><span class="section-num">5</span> {t('AI 스마트 분석', 'AI Smart Analysis')}</h2>
            <p class="guide-text">
                {t(
                    'AI가 여러분의 건강 데이터를 분석하여 맞춤형 건강 조언을 제공합니다.',
                    'AI analyzes your health data to provide personalized health advice.',
                )}
            </p>
            <div class="card-grid cols-2">
                {#each aiFeatures as feat}
                    <div class="guide-card">
                        <h4 class="card-title">{t(feat.ko, feat.en)}</h4>
                        <p class="card-desc">{t(feat.descKo, feat.descEn)}</p>
                    </div>
                {/each}
            </div>
            <h3 class="subsection-title">{t('크레딧 시스템', 'Credit System')}</h3>
            <p class="guide-text">
                {t(
                    'AI 분석은 크레딧을 사용합니다. 크레딧은 활동 포인트로 교환하거나 상점에서 구매할 수 있습니다.',
                    'AI analyses use credits. Credits can be earned from activity points or purchased in the shop.',
                )}
            </p>
            <div class="info-box">
                <svg class="box-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    ><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg
                >
                <span
                    >{t(
                        'AI 분석 결과는 참고 목적으로만 사용하세요. 의료 전문가의 조언을 대체하지 않습니다.',
                        'AI analysis results are for reference only and do not replace professional medical advice.',
                    )}</span
                >
            </div>
        </section>

        <!-- 6. Social -->
        <section id="social" class="guide-section">
            <h2 class="section-title"><span class="section-num">6</span> {t('소셜', 'Social')}</h2>
            <p class="guide-text">
                {t(
                    '건강 관리를 더 재미있게! 포인트, 배지, 랭킹으로 동기부여를 받고, 다른 사용자와 함께 성장하세요.',
                    'Make health management fun! Stay motivated with points, badges, and rankings.',
                )}
            </p>
            <div class="card-grid cols-2">
                {#each socialFeatures as feat}
                    <div class="guide-card">
                        <h4 class="card-title">{t(feat.ko, feat.en)}</h4>
                        <p class="card-desc">{t(feat.descKo, feat.descEn)}</p>
                    </div>
                {/each}
            </div>
        </section>

        <!-- 7. Challenge -->
        <section id="challenge" class="guide-section">
            <h2 class="section-title"><span class="section-num">7</span> {t('챌린지', 'Challenge')}</h2>
            <p class="guide-text">
                {t(
                    '건강 목표를 챌린지로 만들어 참여하세요! 혼자 또는 친구와 함께 도전할 수 있습니다.',
                    'Turn your health goals into challenges! Take on challenges alone or with friends.',
                )}
            </p>
            <h3 class="subsection-title">{t('챌린지 참여 방법', 'How to Join Challenges')}</h3>
            <ol class="steps-list">
                <li>{t('하단 탭에서 챌린지 탭을 선택합니다.', 'Select the Challenge tab from the bottom navigation.')}</li>
                <li>{t('참여 가능 탭에서 원하는 챌린지를 찾습니다.', 'Find a challenge you want in the Available tab.')}</li>
                <li>{t('챌린지 상세 정보를 확인하고 참여하기를 탭합니다.', 'Check the challenge details and tap Join.')}</li>
                <li>{t('진행 중인 챌린지는 활성 탭에서 확인합니다.', 'Track your active challenges in the Active tab.')}</li>
            </ol>
            <h3 class="subsection-title">{t('근거리 P2P 챌린지', 'Nearby P2P Challenge')}</h3>
            <p class="guide-text">
                {t(
                    '주변에 있는 친구와 블루투스로 실시간 대결 챌린지를 즐길 수 있습니다!',
                    'Enjoy real-time challenge competitions with nearby friends via Bluetooth!',
                )}
            </p>
            <ol class="steps-list">
                <li>{t('챌린지 탭에서 근거리 챌린지 버튼을 탭합니다.', 'Tap the Nearby Challenge button in the Challenge tab.')}</li>
                <li>{t('주변 기기를 탐색하고 상대방과 연결합니다.', 'Search for nearby devices and connect with your opponent.')}</li>
                <li>{t('챌린지 종류를 선택하고 대결을 시작합니다.', 'Select the challenge type and start the competition.')}</li>
                <li>{t('결과와 최종 순위를 확인합니다.', 'Check the results and the final ranking.')}</li>
            </ol>
        </section>

        <!-- 8. Voice -->
        <section id="voice" class="guide-section">
            <h2 class="section-title"><span class="section-num">8</span> {t('음성 어시스턴트', 'Voice Assistant')}</h2>
            <p class="guide-text">
                {t(
                    '손을 쓰지 않고 음성으로 건강 데이터를 기록하세요! 화면 하단의 마이크 버튼을 눌러 시작합니다.',
                    'Record health data hands-free using your voice! Tap the microphone button at the bottom to start.',
                )}
            </p>
            <div class="card-grid cols-2">
                <div class="guide-card">
                    <h4 class="card-title">{t('건강 기록', 'Health Recording')}</h4>
                    <ul class="card-list">
                        <li>{t('"혈압 120에 80 기록해줘"', '"Record blood pressure 120 over 80"')}</li>
                        <li>{t('"체중 65kg 기록"', '"Record weight 65kg"')}</li>
                        <li>{t('"체온 36.5도"', '"Temperature 36.5 degrees"')}</li>
                    </ul>
                </div>
                <div class="guide-card">
                    <h4 class="card-title">{t('데이터 조회', 'Data Inquiry')}</h4>
                    <ul class="card-list">
                        <li>{t('"오늘 걸음 수 알려줘"', '"Tell me today\'s step count"')}</li>
                        <li>{t('"최근 혈압 보여줘"', '"Show recent blood pressure"')}</li>
                        <li>{t('"이번 주 운동 요약"', '"This week\'s workout summary"')}</li>
                    </ul>
                </div>
            </div>
            <div class="tip-box">
                <svg class="box-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    ><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg
                >
                <span
                    ><strong>TIP:</strong>
                    {t(
                        '음성 어시스턴트는 한국어, 영어, 일본어, 중국어를 지원합니다.',
                        'The voice assistant supports Korean, English, Japanese, and Chinese.',
                    )}</span
                >
            </div>
        </section>

        <!-- 9. CS Chat -->
        <section id="cs-chat" class="guide-section">
            <h2 class="section-title"><span class="section-num">9</span> {t('고객지원 챗봇', 'Customer Support Chatbot')}</h2>
            <p class="guide-text">
                {t(
                    '앱 사용 중 궁금한 점이 있으면 CS 챗봇에게 물어보세요! AI가 즉시 답변해드립니다.',
                    'Have questions while using the app? Ask the CS Chatbot! Our AI provides instant answers.',
                )}
            </p>
            <ol class="steps-list">
                <li>{t('화면 하단의 채팅 버튼을 탭합니다.', 'Tap the chat button at the bottom of the screen.')}</li>
                <li>
                    {t('궁금한 내용을 입력하거나 자주 묻는 질문에서 선택합니다.', 'Type your question or select from frequently asked questions.')}
                </li>
                <li>{t('AI가 즉시 답변을 제공합니다.', 'AI provides an instant response.')}</li>
            </ol>
            <div class="info-box">
                <svg class="box-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    ><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg
                >
                <span
                    >{t(
                        '챗봇으로 해결되지 않는 문의는 이메일 에스컬레이션을 통해 고객지원 담당자에게 전달됩니다.',
                        'Issues not resolved by the chatbot will be escalated via email to our support team.',
                    )}</span
                >
            </div>
        </section>

        <!-- 10. Settings -->
        <section id="settings" class="guide-section">
            <h2 class="section-title"><span class="section-num">10</span> {t('설정', 'Settings')}</h2>
            <p class="guide-text">
                {t('화면 왼쪽 상단의 메뉴에서 설정으로 이동할 수 있습니다.', 'Access settings from the menu at the top left of the screen.')}
            </p>
            <div class="card-grid cols-3">
                {#each settingsCards as card}
                    <div class="guide-card compact">
                        <h4 class="card-title">{t(card.ko, card.en)}</h4>
                        <p class="card-desc">{t(card.descKo, card.descEn)}</p>
                    </div>
                {/each}
            </div>
        </section>

        <!-- 11. FAQ -->
        <section id="faq" class="guide-section">
            <h2 class="section-title"><span class="section-num">11</span> {t('자주 묻는 질문', 'Frequently Asked Questions')}</h2>
            <div class="faq-list">
                {#each faqItems as item, i}
                    <div class="faq-item" class:open={activeFaq === i}>
                        <button class="faq-question" onclick={() => toggleFaq(i)}>
                            <span>{t(item.qKo, item.qEn)}</span>
                            <svg
                                class="faq-chevron"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"><path d="m6 9 6 6 6-6" /></svg
                            >
                        </button>
                        {#if activeFaq === i}
                            <div class="faq-answer">
                                <p>{t(item.aKo, item.aEn)}</p>
                            </div>
                        {/if}
                    </div>
                {/each}
            </div>
        </section>

        <!-- CTA -->
        <section class="guide-cta">
            <h2 class="cta-title">{t('지금 제이드아이를 시작하세요', 'Start JadEye Now')}</h2>
            <p class="cta-text">{t('건강한 습관, 제이드아이와 함께 만들어가요', 'Build healthy habits with JadEye')}</p>
            <div class="cta-badges">
                <a
                    href="https://play.google.com/store/apps/details?id=com.bjf.jadeye"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="cta-badge-link"
                >
                    <img src="/images/google-play-badge-ko.svg" alt="Google Play" class="cta-badge" />
                </a>
                <a
                    href="https://apps.apple.com/kr/app/%EC%A0%9C%EC%9D%B4%EB%93%9C%EC%95%84%EC%9D%B4/id6759511149"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="cta-badge-link"
                >
                    <img src="/images/app-store-badge-ko.svg" alt="App Store" class="cta-badge appstore" />
                </a>
            </div>
        </section>
    </div>
</div>

<style>
    .guide-page {
        --jade-primary: #356859;
        --jade-dark: #003d32;
        --jade-medium: #7db39a;
        --jade-light: #b8e6d4;
        --jade-pale: #e8f0eb;
        --gold: #f9aa33;
        --gold-pale: #faf6eb;
        --surface: #fafaf8;
        --text: #1a2e22;
        --text-sub: #5a7a62;
        --border: #e5e7e5;
    }

    /* === Hero === */
    .guide-hero {
        padding: 10rem 1.5rem 2.5rem;
        text-align: center;
        background: var(--surface);
        border-bottom: 1px solid var(--border);
    }

    .guide-hero-content {
        max-width: 40rem;
        margin: 0 auto;
    }

    .guide-hero-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        font-size: 0.76rem;
        font-weight: 500;
        color: var(--text-sub);
        border: 1px solid var(--border);
        padding: 0.3rem 0.9rem;
        border-radius: 100px;
        margin-bottom: 1.5rem;
    }

    .guide-hero-title {
        font-size: 2.5rem;
        font-weight: 600;
        color: var(--text);
        letter-spacing: -0.03em;
        margin-bottom: 0.5rem;
    }

    .guide-hero-subtitle {
        font-size: 1rem;
        color: var(--text-sub);
    }

    /* Language Switcher */
    .lang-switcher {
        display: flex;
        gap: 0.35rem;
        justify-content: center;
        margin-bottom: 1.5rem;
    }

    .lang-btn {
        padding: 0.35rem 1rem;
        border-radius: 6px;
        border: 1px solid var(--border);
        background: #fff;
        color: var(--text-sub);
        font-weight: 500;
        font-size: 0.82rem;
        cursor: pointer;
        transition: all 0.2s;
    }

    .lang-btn:hover {
        border-color: var(--jade-medium);
        color: var(--text);
    }

    .lang-btn.active {
        background: var(--jade-dark);
        border-color: var(--jade-dark);
        color: #fff;
    }

    /* === TOC === */
    .guide-toc {
        max-width: 56rem;
        margin: 0 auto;
        padding: 1.5rem 1.5rem;
    }

    .toc-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
    }

    .toc-item {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.4rem 0.9rem;
        border-radius: 6px;
        border: 1px solid var(--border);
        background: #fff;
        color: var(--text-sub);
        font-size: 0.82rem;
        font-weight: 450;
        cursor: pointer;
        transition: all 0.2s;
    }

    .toc-item:hover {
        border-color: var(--jade-medium);
        color: var(--text);
    }

    .toc-num {
        font-size: 0.72rem;
        font-weight: 600;
        color: var(--jade-medium);
    }

    /* === Guide articles (애드센스 재심사 대응 작업 4) === */
    .guide-articles {
        max-width: 56rem;
        margin: 0 auto;
        padding: 1.5rem 1.5rem 0;
    }

    .guide-articles-title {
        font-size: 1.15rem;
        font-weight: 650;
        color: var(--text);
    }

    .guide-articles-subtitle {
        margin-top: 0.3rem;
        font-size: 0.88rem;
        color: var(--text-sub);
    }

    .guide-articles-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
        gap: 0.75rem;
        margin: 1rem 0 0;
        padding: 0;
        list-style: none;
    }

    .guide-article-card {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
        height: 100%;
        padding: 1rem 1.1rem;
        border-radius: 10px;
        border: 1px solid var(--border);
        background: #fff;
        color: var(--text);
        text-decoration: none;
        transition: border-color 0.2s;
    }

    .guide-article-card:hover {
        border-color: var(--jade-medium);
    }

    .guide-article-name {
        font-size: 0.95rem;
        font-weight: 600;
        line-height: 1.45;
        color: var(--jade-dark);
    }

    .guide-article-desc {
        font-size: 0.82rem;
        line-height: 1.6;
        color: var(--text-sub);
    }

    .guide-article-date {
        margin-top: auto;
        font-size: 0.75rem;
        color: var(--text-sub);
    }

    /* === Content === */
    .guide-content {
        max-width: 56rem;
        margin: 0 auto;
        padding: 0 1.5rem 3rem;
    }

    .guide-section {
        margin-bottom: 2.5rem;
        scroll-margin-top: 5rem;
    }

    .section-title {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        font-size: 1.5rem;
        font-weight: 600;
        color: var(--text);
        margin-bottom: 1rem;
        padding-bottom: 0.6rem;
        border-bottom: 1px solid var(--border);
        letter-spacing: -0.02em;
    }

    .section-num {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        background: var(--jade-dark);
        color: #fff;
        border-radius: 6px;
        font-size: 0.85rem;
        font-weight: 600;
        flex-shrink: 0;
    }

    .subsection-title {
        font-size: 1.15rem;
        font-weight: 600;
        color: var(--jade-primary);
        margin: 1.5rem 0 0.6rem;
    }

    .subsection-title-sm {
        font-size: 1rem;
        font-weight: 600;
        color: var(--text);
        margin: 1.25rem 0 0.6rem;
    }

    .guide-text {
        font-size: 0.95rem;
        color: var(--text-sub);
        line-height: 1.7;
        margin-bottom: 0.75rem;
    }

    /* === Steps === */
    .steps-list {
        counter-reset: step;
        list-style: none;
        padding: 0;
        margin: 0.75rem 0;
    }

    .steps-list li {
        counter-increment: step;
        position: relative;
        padding: 0.7rem 0.75rem 0.7rem 3rem;
        margin-bottom: 0.4rem;
        background: #fff;
        border-radius: 8px;
        border: 1px solid var(--border);
        font-size: 0.9rem;
        color: var(--text);
        line-height: 1.6;
    }

    .steps-list li::before {
        content: counter(step);
        position: absolute;
        left: 0.75rem;
        top: 50%;
        transform: translateY(-50%);
        width: 1.6rem;
        height: 1.6rem;
        background: var(--jade-pale);
        color: var(--jade-primary);
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 0.78rem;
    }

    /* === Cards === */
    .card-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 0.75rem;
        margin: 0.75rem 0;
    }

    .card-grid.cols-2 {
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    }

    .card-grid.cols-3 {
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }

    .guide-card {
        background: #fff;
        padding: 1.25rem;
        border-radius: 10px;
        border: 1px solid var(--border);
        transition: all 0.2s;
    }

    .guide-card:hover {
        border-color: var(--jade-medium);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
    }

    .guide-card.compact {
        padding: 1rem;
    }

    .card-title {
        font-size: 0.95rem;
        color: var(--text);
        margin-bottom: 0.35rem;
        font-weight: 600;
    }

    .card-desc {
        font-size: 0.85rem;
        color: var(--text-sub);
        line-height: 1.6;
    }

    .card-list {
        list-style: none;
        padding: 0;
        margin: 0.25rem 0 0;
    }

    .card-list li {
        padding: 0.2rem 0 0.2rem 1.2rem;
        position: relative;
        font-size: 0.85rem;
        color: var(--text-sub);
        line-height: 1.5;
    }

    .card-list li::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0.6rem;
        width: 5px;
        height: 5px;
        background: var(--jade-medium);
        border-radius: 50%;
    }

    /* === Tab Preview === */
    .tab-preview {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 0.5rem;
        margin: 0.75rem 0;
    }

    .tab-item {
        background: #fff;
        border: 1px solid var(--border);
        border-radius: 8px;
        padding: 0.65rem 0.5rem;
        text-align: center;
        transition: all 0.2s;
    }

    .tab-item:hover {
        border-color: var(--jade-medium);
    }

    .tab-label {
        font-size: 0.78rem;
        font-weight: 500;
        color: var(--text);
    }

    /* === Boxes === */
    .tip-box,
    .warning-box,
    .info-box {
        display: flex;
        align-items: flex-start;
        gap: 0.6rem;
        padding: 0.75rem 1rem;
        border-radius: 8px;
        margin: 0.75rem 0;
        font-size: 0.85rem;
        line-height: 1.6;
    }

    .box-icon {
        flex-shrink: 0;
        margin-top: 0.15rem;
    }

    .tip-box {
        background: var(--jade-pale);
        border-left: 3px solid var(--jade-primary);
        color: var(--jade-dark);
    }

    .warning-box {
        background: var(--gold-pale);
        border-left: 3px solid var(--gold);
        color: #7a5a00;
    }

    .info-box {
        background: #eef4ff;
        border-left: 3px solid #5b8dd9;
        color: #2a4a7f;
    }

    /* === FAQ === */
    .faq-list {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
    }

    .faq-item {
        border: 1px solid var(--border);
        border-radius: 8px;
        overflow: hidden;
        transition: all 0.2s;
    }

    .faq-item.open {
        border-color: var(--jade-medium);
    }

    .faq-question {
        width: 100%;
        background: #fff;
        padding: 0.75rem 1rem;
        font-weight: 500;
        color: var(--text);
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        transition: background 0.2s;
        border: none;
        text-align: left;
        font-size: 0.9rem;
    }

    .faq-question:hover {
        background: var(--jade-pale);
    }

    .faq-chevron {
        color: var(--text-sub);
        transition: transform 0.3s;
        flex-shrink: 0;
    }

    .faq-item.open .faq-chevron {
        transform: rotate(180deg);
    }

    .faq-answer {
        padding: 0.75rem 1rem;
        border-top: 1px solid var(--border);
        animation: fadeIn 0.25s ease;
    }

    .faq-answer p {
        font-size: 0.88rem;
        color: var(--text-sub);
        line-height: 1.7;
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(-4px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    /* === CTA === */
    .guide-cta {
        margin-top: 1.5rem;
        padding: 2.5rem 2rem;
        background: linear-gradient(135deg, var(--jade-dark), #2d4a36);
        border-radius: 14px;
        text-align: center;
        color: #fff;
    }

    .cta-title {
        font-size: 1.5rem;
        font-weight: 600;
        margin-bottom: 0.4rem;
        letter-spacing: -0.02em;
    }

    .cta-text {
        color: rgba(255, 255, 255, 0.55);
        font-size: 0.9rem;
        margin-bottom: 1.5rem;
    }

    .cta-badges {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.6rem;
        align-items: center;
    }

    .cta-badge-link {
        display: flex;
        align-items: center;
        transition: transform 0.2s;
    }

    .cta-badge-link:hover {
        transform: scale(1.04);
    }

    .cta-badge {
        height: 3.5rem;
        object-fit: contain;
    }

    /* === Responsive === */
    @media (max-width: 768px) {
        .guide-hero {
            padding: 10rem 1rem 2rem;
        }
        .guide-hero-title {
            font-size: 2rem;
        }
        .section-title {
            font-size: 1.25rem;
        }
        .tab-preview {
            grid-template-columns: repeat(3, 1fr);
        }
    }

    @media (max-width: 480px) {
        .tab-preview {
            grid-template-columns: repeat(2, 1fr);
        }
        .card-grid {
            grid-template-columns: 1fr;
        }
        .card-grid.cols-2,
        .card-grid.cols-3 {
            grid-template-columns: 1fr;
        }
        .toc-grid {
            gap: 0.3rem;
        }
    }

    @media (min-width: 1024px) {
        .guide-hero {
            padding: 14rem 2.5rem 2.5rem;
        }
    }
</style>
